import { request } from "./request";

/**
 * GDPR self-serve endpoints (SAR export + right-to-erasure).
 *
 * The backend router mounts these at `/api/gdpr/*`; `API_BASE_URL` already
 * carries the `/api` prefix, so the paths here are `/gdpr/*`.
 *
 * The delete endpoint requires an explicit confirmation token in the body; the
 * exact literal is validated server-side (`backend/src/routes/gdpr.ts`) and must
 * not be localised or altered.
 */

/** Confirmation literal required by `DELETE /api/gdpr/delete`. */
export const GDPR_DELETE_CONFIRMATION = "DELETE_MY_DATA";

/**
 * Subject Access Request export. The payload is the full JSON document returned
 * by the server (audit logs + retention metadata); callers typically hand it
 * straight to a file download, so it is intentionally left opaque here.
 */
export async function exportUserData(): Promise<unknown> {
  return request<unknown>("/gdpr/export");
}

/** Result of the erasure request (soft-delete/anonymisation summary). */
export interface DeleteAccountResult {
  success: boolean;
  deletedAt: string;
  affectedSources: string[];
  recordsAffected: number;
  deletionType: string;
}

/**
 * Request erasure of the current user's data. Rate limited to once/day
 * server-side (surfaces as an {@link import("./errors").ApiError} with status
 * 429). The caller is responsible for signing the user out afterwards.
 */
export async function deleteAccount(): Promise<DeleteAccountResult> {
  return request<DeleteAccountResult>("/gdpr/delete", {
    method: "DELETE",
    body: { confirmation: GDPR_DELETE_CONFIRMATION },
    contentType: "json",
  });
}
